import { Box, Container, Typography, useTheme } from "@mui/material";
import { ReactNode } from "react";

type PolicyPageLayoutProps = {
  title: string;
  children: ReactNode;
};

export const PolicySection = ({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) => {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";

  return (
    <Box sx={{ mb: 4 }}>
      {title && (
        <Typography
          component="h2"
          sx={{
            mb: 1.25,
            color: dark ? "#f3f5f4" : "#101813",
            fontSize: {
              xs: `calc(19px * var(--dynamic-font-size, 1))`,
              md: `calc(24px * var(--dynamic-font-size, 1))`,
            },
            fontWeight: 700,
          }}
        >
          {title}
        </Typography>
      )}
      <Typography
        component="div"
        sx={{
          color: dark ? "#c7cdca" : "#26342b",
          fontSize: {
            xs: `calc(14px * var(--dynamic-font-size, 1))`,
            md: `calc(16px * var(--dynamic-font-size, 1))`,
          },
          lineHeight: 1.75,
          textAlign: { xs: "left", md: "justify" },
        }}
      >
        {children}
      </Typography>
    </Box>
  );
};

const PolicyPageLayout = ({ title, children }: PolicyPageLayoutProps) => {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        pt: "45px",
        pb: { xs: 7, md: 10 },
        background: "transparent",
      }}
    >
      <Container
        maxWidth={false}
        sx={{ width: { xs: "100%", sm: "94%", md: "88%" } }}
      >
        <Typography
          component="h1"
          sx={{
            mb: { xs: 4, md: 5 },
            pl: { md: 1 },
            color: dark ? "#f5f7f6" : "#0f202b",
            fontSize: {
              xs: `calc(32px * var(--dynamic-font-size, 1))`,
              md: `calc(46px * var(--dynamic-font-size, 1))`,
            },
            fontWeight: 700,
            lineHeight: 1.15,
          }}
        >
          {title}
        </Typography>

        <Box
          component="article"
          sx={{
            px: { xs: 3, sm: 5, md: 7 },
            py: { xs: 4, md: 5.5 },
            border: `1.5px solid ${dark ? "#5f6864" : "#61dfd1"}`,
            borderRadius: { xs: "30px", md: "62px" },
            background: dark
              ? "linear-gradient(135deg, rgba(42,46,49,.97), rgba(33,43,39,.95))"
              : "linear-gradient(135deg, rgba(255,255,255,.95), rgba(235,248,255,.9) 48%, rgba(240,255,231,.92))",
            "& > :last-child": { mb: 0 },
          }}
        >
          {children}
        </Box>
      </Container>
    </Box>
  );
};

export default PolicyPageLayout;
